import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Tooltip } from "@mui/material";
import { TableDisponibilidadeFrota } from "components/ModalHistoricoViagensFrota/ModalHistoricoUtils.ts";
import ModalHistoricoTabela from "components/ModalHistoricoViagensFrota/ModalHistoricoViagensFrota.tsx";
import { anoDoisMilString } from "core/utils/UtilityDates.ts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TabelaVirtualizadaGenerica } from "../../../components/TabelaVirtualizadaGenerica/TabelaVirtualizadaGenerica.tsx";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import { useToast } from "../../../contexts/ToastContext.tsx";
import MotionContainer from "../../../core/components/MotionContainer.tsx";
import MotionSemAutorizacao from "../../../core/components/MotionSemAutorizacao.tsx";
import Separator from "../../../core/components/Separator.tsx";
import Layout from "../../../core/layout/Layout.tsx";
import useService from "../../../hooks/useService.ts";
import { Motorista } from "../../../models/Motorista.ts";
import { getDisponibilidadeFrota } from "../../../services/ModuloGerenciador/DisponibilidadeFrota.ts";
import "./DisponibilidadeFrota.css";
import FiltrosDisponibilidadeFrota from "./FiltrosDisponibilidadeFrota/FiltrosDisponibilidadeFrota.tsx";

export type ViagemMarcadaOption = 1 | 2 | null;

export type DisponibilidadeFrotaFiltros = {
	viagemMarcada: ViagemMarcadaOption;
	diasSemCarregar: number | null;
	eixos: number;
	dataProximaViagem: string;
	tipoCarroceriaId: number;
	elegibilidadeBrf: number;
	contexto: string;
};

function readFromStorage(): DisponibilidadeFrotaFiltros {
	try {
		if (typeof window === "undefined") return emptyDisponibilidadeFrotaFiltros;
		const raw = window.sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return emptyDisponibilidadeFrotaFiltros;
		const parsed = JSON.parse(raw) as Partial<DisponibilidadeFrotaFiltros>;
		return {
			...emptyDisponibilidadeFrotaFiltros,
			...parsed,
		};
	} catch {
		return emptyDisponibilidadeFrotaFiltros;
	}
}

const STORAGE_KEY = "disponibilidade_frota_filtros_v1";

export const emptyDisponibilidadeFrotaFiltros: DisponibilidadeFrotaFiltros = {
	viagemMarcada: 1,
	diasSemCarregar: 5,
	eixos: 0,
	dataProximaViagem: "",
	tipoCarroceriaId: 0,
	elegibilidadeBrf: 0,
	contexto: "todos",
};

function DisponibilidadeFrota() {
	const { profile, hasRegra } = useProfile();
	const { showToast } = useToast();

	const hasBuscadoDisponibilidadeFrota = useRef(false);

	const [dadosTabela, setDadosTabela] = useState<Motorista[]>([]);
	const [filtros, setFiltros] = useState<DisponibilidadeFrotaFiltros>(() =>
		readFromStorage(),
	);
	const [openHistorico, setOpenHistorico] = useState(false);
	const [cteIdSelecionado, setCteIdSelecionado] = useState<number | null>(null);

	const [isLoadingDisponibilidadeFrota, buscarDisponibilidadeFrota] =
		useService(getDisponibilidadeFrota, {
			autoStart: false,
			onData: (response) => {
				setDadosTabela(response);
			},
			onError: (error) => {
				if (typeof error.response.data === "string") {
					showToast(error.response.data, "error");
				} else {
					showToast(
						"Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte.",
						"error",
					);
				}
			},
		});

	const abrirWhats = useCallback((phoneNumber: string) => {
		const cleanedNumber = phoneNumber.replace(/\D/g, "");
		const formattedNumber = cleanedNumber.startsWith("55")
			? cleanedNumber
			: `55${cleanedNumber}`;
		const url = `https://wa.me/${formattedNumber}`;
		window.open(url, "_blank");
	}, []);

	const handleClickProp = useCallback(async (row: Motorista) => {
		setCteIdSelecionado((row as any).idMotorista);
		setOpenHistorico(true);
	}, []);

	const renderCell = useCallback(
		(coluna: any, row: any) => {
			if (coluna.objetoEndereco === "true") {
				const endereco = row.endereco;
				if (!endereco) return "";

				return `${endereco.rua ?? ""}, ${endereco.numero ?? ""}, ${
					endereco.bairro ?? ""
				} - ${endereco.cidade ?? ""}`;
			}
			if (coluna.field === "telefoneMotorista") {
				return (
					<div
						style={{
							display: "flex",
							gap: "4px",
							cursor: row.telefoneMotorista ? "pointer" : "default",
						}}
						onClick={() => {
							abrirWhats(row.telefoneMotorista);
						}}
					>
						{row.telefoneMotorista}
						<Tooltip title="Contato Motorista" disableInteractive>
							<WhatsAppIcon
								sx={{ height: 15, color: "#25D366", cursor: "pointer" }}
							/>
						</Tooltip>
					</div>
				);
			}
			if (coluna.field === "motorista" && coluna.link) {
				return (
					<span className="link-padrao" onClick={() => handleClickProp(row)}>
						<Tooltip
							placement="top"
							title={String(row.motorista)}
							key={String(coluna.field)}
						>
							{row.motorista}
						</Tooltip>
					</span>
				);
			}

			return undefined;
		},
		[abrirWhats, handleClickProp],
	);

	const handleBuscarDisponibilidadeFrota = useCallback(() => {
		if (profile?.token) {
			buscarDisponibilidadeFrota({
				token: profile?.token,
				dias: filtros.diasSemCarregar || 0,
				eixos: filtros.eixos || 0,
				status: filtros.viagemMarcada || 0,
				tipoCarroceriaId: filtros.tipoCarroceriaId || 0,
				data: !!filtros.dataProximaViagem
					? filtros.dataProximaViagem
					: anoDoisMilString,
				elegivelBrf: filtros.elegibilidadeBrf || 0,
				contexto: filtros.contexto || "todos",
			});
		}
	}, [profile?.token, filtros, buscarDisponibilidadeFrota]);

	const buscarDisponibilidadeFrotaInicial = useCallback(() => {
		if (profile?.token) {
			buscarDisponibilidadeFrota({
				token: profile?.token,
				dias: 5,
				eixos: 0,
				status: 1,
				tipoCarroceriaId: 0,
				data: anoDoisMilString,
				elegivelBrf: 0,
				contexto: "todos",
			});
		}
	}, [profile?.token, buscarDisponibilidadeFrota]);

	const handleClearFiltro = useCallback(() => {
		setFiltros(emptyDisponibilidadeFrotaFiltros);
		buscarDisponibilidadeFrotaInicial();
	}, [buscarDisponibilidadeFrotaInicial]);

	const setFiltrosPatch = useCallback(
		(patch: Partial<DisponibilidadeFrotaFiltros>) => {
			setFiltros((prev) => ({ ...prev, ...patch }));
		},
		[],
	);

	const biggerDevice = useMemo(() => {
		return window.innerWidth >= 1450;
	}, []);

	const [semAutorizacao, setSemAutorizacao] = useState(false);

	useEffect(() => {
		if (profile?.token && !hasBuscadoDisponibilidadeFrota.current) {
			setSemAutorizacao(!hasRegra("Disponibilidade Frota"));
			handleBuscarDisponibilidadeFrota();
			hasBuscadoDisponibilidadeFrota.current = true;
		}
	}, [profile?.token, hasRegra, handleBuscarDisponibilidadeFrota]);

	useEffect(() => {
		window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filtros));
	}, [filtros]);

	const isLoading = useMemo(() => {
		return (
			isLoadingDisponibilidadeFrota ||
			!profile?.token ||
			(!dadosTabela.length && !hasBuscadoDisponibilidadeFrota.current)
		);
	}, [isLoadingDisponibilidadeFrota, dadosTabela.length, profile?.token]);

	if (semAutorizacao) {
		return <MotionSemAutorizacao />;
	}

	return (
		<Layout title="Disponibilidade da Frota">
			<MotionContainer animationKey="DisponibilidadeFrotaScreenContainer">
				<div className="content-container">
					<FiltrosDisponibilidadeFrota
						buscarDisponibilidadeFrota={handleBuscarDisponibilidadeFrota}
						handleClearFiltro={handleClearFiltro}
						filtros={filtros}
						setFiltros={setFiltrosPatch}
						loading={isLoading}
						qtVeiculos={dadosTabela.length}
					/>
					<Separator size="md" />
					<TabelaVirtualizadaGenerica
						dados={dadosTabela}
						colunas={TableDisponibilidadeFrota}
						rowHeight={20}
						isLoading={isLoading}
						renderCell={renderCell}
						onRowClick={() => {}}
						centralized={biggerDevice}
						hasFooter={false}
					/>
					<Separator size="md" />
				</div>
			</MotionContainer>
			<ModalHistoricoTabela
				open={openHistorico}
				onClose={() => setOpenHistorico(false)}
				token={profile?.token}
				cteId={cteIdSelecionado || 0}
				titulo="Histórico de viagens"
			/>
		</Layout>
	);
}

export default DisponibilidadeFrota;
